import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

type UploadSessionResponse = {
  msg: string;
  uploadId: string;
  totalChunks: number;
};

type UploadChunkResponse = {
  msg: string;
  uploadId: string;
  receivedChunks: number;
  totalChunks: number;
};

export type PdfUploadProgress = {
  phase: 'uploading' | 'processing';
  uploadedChunks: number;
  totalChunks: number;
  percent: number;
  message: string;
};

export type PdfDocument = {
  _id: string;
  name: string;
  nameForUser: string;
  uploadedAt: string;
  status: 'chunked' | 'indexed';
  metadata: {
    totalPages: number;
    extractedTitle?: string;
  };
};

export type PdfChapter = {
  _id: string;
  pdfDocumentId: string;
  chapterNumber: number;
  title: string;
  pageRange: {
    start: number;
    end: number;
  };
  tags: string[];
  summary?: string;
  autoExtracted: boolean;
  approvedAt?: string;
};

export type PdfDocumentUsage = {
  fallaufgabenId: string;
  name: string;
  version: number;
  versionsUnique: string;
  state: 'draft' | 'live' | 'archived';
  category: string;
  type: 'exam' | 'vc';
};

export type PdfChunk = {
  _id: string;
  sequenceNum: number;
  pageStart: number;
  pageEnd: number;
  content: string;
};

@Injectable({
  providedIn: 'root'
})
export class PdfAdminService {
  private http = inject(HttpClient);
  private pdfBaseRoute = '/api/admin/pdf';
  private readonly chunkSizeBytes = 2 * 1024 * 1024;

  public async uploadPdf(file: File, onProgress?: (progress: PdfUploadProgress) => void) {
    const totalChunks = Math.max(1, Math.ceil(file.size / this.chunkSizeBytes));

    const session = await firstValueFrom(this.http.post<UploadSessionResponse>(
      `${this.pdfBaseRoute}/upload/session`,
      {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        totalChunks
      }
    ));

    onProgress?.({
      phase: 'uploading',
      uploadedChunks: 0,
      totalChunks,
      percent: 0,
      message: 'Upload vorbereitet'
    });

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * this.chunkSizeBytes;
      const end = Math.min(start + this.chunkSizeBytes, file.size);
      const chunkBlob = file.slice(start, end, file.type || 'application/pdf');

      const chunkFormData = new FormData();
      chunkFormData.append('uploadId', session.uploadId);
      chunkFormData.append('chunkIndex', String(chunkIndex));
      chunkFormData.append('totalChunks', String(totalChunks));
      chunkFormData.append('chunk', chunkBlob, `${file.name}.part-${chunkIndex}`);

      const chunkResult = await firstValueFrom(this.http.post<UploadChunkResponse>(
        `${this.pdfBaseRoute}/upload/chunk`,
        chunkFormData
      ));

      const percent = Math.min(
        95,
        Math.round((chunkResult.receivedChunks / totalChunks) * 95)
      );

      onProgress?.({
        phase: 'uploading',
        uploadedChunks: chunkResult.receivedChunks,
        totalChunks,
        percent,
        message: `${chunkResult.receivedChunks}/${totalChunks} Chunks hochgeladen`
      });
    }

    onProgress?.({
      phase: 'processing',
      uploadedChunks: totalChunks,
      totalChunks,
      percent: 97,
      message: 'Datei wird serverseitig verarbeitet'
    });

    const result = await firstValueFrom(this.http.post<{
      msg: string;
      pdfDocumentId: string;
      totalPages: number;
      extractedChars: number;
      chunks: number;
    }>(`${this.pdfBaseRoute}/upload/complete`, {
      uploadId: session.uploadId
    }));

    onProgress?.({
      phase: 'processing',
      uploadedChunks: totalChunks,
      totalChunks,
      percent: 100,
      message: 'Verarbeitung abgeschlossen'
    });

    return result;
  }

  public async listPdfs() {
    return await firstValueFrom(this.http.get<PdfDocument[]>(`${this.pdfBaseRoute}/list`));
  }

  public async deletePdf(pdfId: string) {
    return await firstValueFrom(this.http.delete<{ msg: string }>(`${this.pdfBaseRoute}/${pdfId}`));
  }

  public async getChunks(pdfId: string) {
    return await firstValueFrom(this.http.get<PdfChunk[]>(`${this.pdfBaseRoute}/${pdfId}/chunks`));
  }

  public async getChapters(pdfId: string) {
    return await firstValueFrom(this.http.get<PdfChapter[]>(`${this.pdfBaseRoute}/${pdfId}/chapters`));
  }

  public async getDocumentUsages(pdfId: string) {
    return await firstValueFrom(this.http.get<PdfDocumentUsage[]>(`${this.pdfBaseRoute}/${pdfId}/usages`));
  }

  public async updatePdfDocumentName(pdfId: string, nameForUser: string) {
    return await firstValueFrom(this.http.put<{ msg: string; document: PdfDocument }>(`${this.pdfBaseRoute}/${pdfId}`, {
      nameForUser
    }));
  }
}
